import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function InteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Interior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Ventura', 'Thousand Oaks'],
    description: 'Professional mobile interior car detailing services including deep cleaning, shampooing, steam sanitizing, and leather conditioning.'
  };
  const faqs = [{
    question: 'What does interior detailing include?',
    answer: 'Our interior detail includes deep vacuuming, shampooing of carpets and fabric seats, leather cleaning and conditioning, steam sanitizing of vents and high-touch surfaces, plastic and vinyl restoration, and streak-free interior glass.'
  }, {
    question: 'How long does interior detailing take?',
    answer: 'Interior detailing typically takes 2\u20133 hours depending on the size and condition of your vehicle. Heavily soiled interiors or pet hair removal may take longer.'
  }, {
    question: 'Can you remove stains and odors?',
    answer: 'Yes. Our shampooing and steam extraction lift most deep-set stains, and our sanitizing process neutralizes odors from spills, smoke, and pets.'
  }, {
    question: 'How much does interior detailing cost in Ventura County?',
    answer: 'Interior detailing starts at $180, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you come to my home or office?',
    answer: 'Yes — we are fully mobile, bringing our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Interior Detailing',
    item: 'https://www.fulldetailpro.com/services/interior-detailing'
  }];
  return <ServicePageLayout title="Interior Detailing Ventura County" subtitle="Restore your vehicle's cabin to a factory-fresh feel with our comprehensive mobile interior detailing service. We come directly to your home or office." metaDescription="Expert mobile interior detailing in Ventura County \u2014 Camarillo, Ventura & Thousand Oaks. Deep cleaning, stain removal & steam sanitizing. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-990" data-mp="h575qw2o8dhh">
      <p data-id="element-991" data-mp="3plsf4dz7t8l4">
        Your vehicle's interior is where you spend all your time driving. Over
        time, dirt, dust, spills, pet hair, and odors can accumulate, making
        your daily commute less enjoyable and potentially impacting your health.
        At Mike's Pro Detail, our{' '}
        <strong data-id="element-992" data-mp="1cfvk6puc97lh">mobile interior car detailing</strong>{' '}
        service is designed to deep clean, sanitize, and restore your cabin to a
        like-new condition. Whether you're in Camarillo CA, Thousand Oaks, or
        anywhere in Ventura County, our fully equipped mobile unit brings
        premium interior detailing directly to your doorstep.
      </p>

      <h2 data-id="element-993" data-mp="17grazz7ez9v5">What Our Interior Detailing Includes</h2>
      <p data-id="element-994" data-mp="1v5k29id3ctjf">
        We don't just wipe down surfaces; we perform a meticulous, multi-step
        deep cleaning process to ensure every crack and crevice is addressed.
        Our comprehensive interior detail includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-995" data-mp="1lfiiv3vonzau">
        <li className="flex items-start" data-id="element-996" data-mp="1qf3n9ttmgzuy">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-997" data-mp="1qe26r9tu7l5u" />
          <span data-id="element-998" data-mp="2e4pjq9im10z9">
            <strong data-id="element-999" data-mp="2e3witp6c15p1">Thorough Vacuuming:</strong> Deep extraction of dirt,
            debris, and pet hair from carpets, seats, floor mats, and the trunk
            area.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1000" data-mp="v8e5rrhztjcq">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1001" data-mp="v500obhy2w1l" />
          <span data-id="element-1002" data-mp="q6orm2zh4pkf">
            Cleaning of fabric seats, carpets, and floor mats to lift deep-set
            stains and grime.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1003" data-mp="galpgx4mgqxh">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1004" data-mp="gbn2v8941pid" />
          <span data-id="element-1005" data-mp="1dy3xg5qglp0f">
            <strong data-id="element-1006" data-mp="1dwj3oibyya3m">Leather Cleaning & Conditioning:</strong> Safe, pH-balanced
            cleaning of leather seats followed by a premium conditioner to
            prevent cracking and fading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1007" data-mp="145y5ux44d9lr">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1008" data-mp="z9hk29ke77bu" />
          <span data-id="element-1009" data-mp="z73n1v65jl1n">
            <strong data-id="element-1010" data-mp="1wo254kqchg3j">Steam Cleaning & Sanitization:</strong> High-pressure steam
            is used on vents, cup holders, and high-touch areas to kill bacteria
            and melt away sticky residue.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1011" data-mp="207aywq00mxjy">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1012" data-mp="208m32qxdi7bl" />
          <span data-id="element-1013" data-mp="248d0hdhenlp3">
            <strong data-id="element-1014" data-mp="249xjc5ucxg74">Plastics & Vinyl Restoration:</strong> Cleaning and UV
            protection applied to the dashboard, door panels, and center console
            to restore a rich, non-greasy finish.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1015" data-mp="2j5dgijq8hs7q">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1016" data-mp="2j4c348lqwtmu" />
          <span data-id="element-1017" data-mp="2n5xf7rqgh3vz">
            <strong data-id="element-1018" data-mp="2n5e8762cfzwq">Streak-Free Windows:</strong> Interior glass and mirrors are
            cleaned to a crystal-clear, streak-free shine for optimal
            visibility.
          </span>
        </li>
      </ul>

      <h2 data-id="element-1019" data-mp="2ww0j4mz48293">The Benefits of Professional Interior Detailing</h2>
      <p data-id="element-1020" data-mp="31w4y28kdkox7">
        Investing in a professional interior detail goes beyond just aesthetics.
        A clean interior significantly improves the air quality inside your
        vehicle by removing dust mites, allergens, and bacteria trapped in the
        upholstery and ventilation system. Furthermore, regular conditioning of
        leather and UV protection on plastics prevents premature aging,
        cracking, and fading caused by the intense Southern California sun. If
        you're planning to sell your vehicle, a pristine interior is one of the
        most effective ways to maximize its resale value.
      </p>

      <h2 data-id="element-1021" data-mp="2zvqugdjv7jk6">Why Choose Mike's Pro Detail for Interior Detailing?</h2>
      <p data-id="element-1022" data-mp="2uygx8bq8i3ls">
        When searching for "Mobile Auto Detailing Camarillo CA" or "Car
        Detailing Camarillo", you want a service that is reliable, meticulous,
        and convenient. Mike's Pro Detail stands out because we use only
        premium, eco-friendly products and professional-grade equipment steam
        cleaners. We understand that your time is valuable, which is why our
        mobile detailing Ventura County service comes entirely to you—fully
        equipped with our own water and detailing equipment. We require access
        to a standard electrical outlet at the service location. From Camarillo
        and Ventura to Thousand Oaks, Simi Valley, and Westlake Village, we
        deliver 5-star results without you ever having to leave your home or
        office.
      </p>

      <h2 data-id="element-1023" data-mp="3he7fmpjgj4u8">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-1024" data-mp="3hd630gmsmaae">
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1025" data-mp="33dwtfzt3eefe">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1026" data-mp="2tiwccl8vto0g">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1027" data-mp="wngt8i0y5eok">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1028" data-mp="36urb63mix6od">
          Headlight Restoration &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}